import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboarding } from './OnboardingContext';

/**
 * RevealSpread — an open two-page cream spread that:
 *   1. Fades in a personalised greeting.
 *   2. Cross-fades to the user's custom focus/goal block.
 *   3. Reveals a glowing "I'm ready" button.
 *   4. On click → tight zoom into the gutter + full white-light wash → enter site.
 */
export default function RevealSpread() {
    const { user, setPhase } = useOnboarding();
    const [stage, setStage] = useState('greeting'); // greeting | goal
    const [showReady, setShowReady] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const [zoom, setZoom] = useState(false);

    const firstName = user?.name?.split(' ')[0] || 'reader';
    const grade = user?.education || 'study';

    useEffect(() => {
        const t1 = setTimeout(() => setStage('goal'), 2600);
        const t2 = setTimeout(() => setShowReady(true), 4000);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, []);

    function enter() {
        setZoom(true);
        setTimeout(() => setLeaving(true), 500); // start white wash after zoom begins
        setTimeout(() => setPhase('inside'), 1400); // enter site after wash
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex items-center justify-center z-40 px-4 overflow-hidden"
            data-testid="reveal-spread"
            style={{ contain: 'layout' }}
        >
            {/* Desktop: 2-page spread */}
            <motion.div
                animate={zoom ? { scale: 1.9, filter: 'brightness(1.4)' } : { scale: 1 }}
                transition={{ duration: 1.0, ease: [0.6, 0, 0.2, 1] }}
                className="relative folio-shadow rounded-sm hidden md:flex"
                style={{
                    width: 'min(1100px, 90vw)',
                    maxHeight: '82vh',
                    aspectRatio: '1.6 / 1',
                    transformOrigin: 'center center',
                }}
            >
                {/* LEFT cream page */}
                <div className="relative w-1/2 h-full paper grain-light rounded-l-sm overflow-hidden">
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-20"
                         style={{ background: 'linear-gradient(to left, rgba(59,68,30,0.3), transparent)' }} />
                    <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
                        <AnimatePresence mode="wait">
                            {stage === 'greeting' ? (
                                <motion.div
                                    key="greeting"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -12 }}
                                    transition={{ duration: 1.0, ease: [0.83, 0, 0.17, 1] }}
                                >
                                    <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-4">
                                        A folio, freshly bound
                                    </div>
                                    <h2
                                        className="display text-[color:var(--ink)]"
                                        style={{ fontSize: 'clamp(30px, 3.6vw, 52px)', lineHeight: 1.05 }}
                                    >
                                        Welcome,{' '}
                                        <span className="display-italic" style={{ color: 'var(--lavender-deep)' }}>
                                            {firstName}.
                                        </span>
                                    </h2>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="grade"
                                    initial={{ opacity: 0, y: 16 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 1.0, ease: [0.83, 0, 0.17, 1] }}
                                >
                                    <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-4">
                                        Prepared for
                                    </div>
                                    <h2
                                        className="display text-[color:var(--ink)]"
                                        style={{ fontSize: 'clamp(26px, 3vw, 44px)', lineHeight: 1.08 }}
                                    >
                                        your{' '}
                                        <span className="display-italic" style={{ color: 'var(--lavender-deep)' }}>
                                            {grade}
                                        </span>{' '}
                                        goals.
                                    </h2>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Spine */}
                <div className="w-[3px] h-full z-[5]"
                     style={{ background: 'linear-gradient(90deg, rgba(59,68,30,0.35), rgba(0,0,0,0.6), rgba(59,68,30,0.35))' }} />

                {/* RIGHT cream page — goal block + I'm ready button */}
                <div className="relative w-1/2 h-full paper grain-light rounded-r-sm overflow-hidden">
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-20"
                         style={{ background: 'linear-gradient(to right, rgba(59,68,30,0.3), transparent)' }} />
                    <div className="relative h-full flex flex-col items-center justify-center p-10 text-center">
                        <AnimatePresence>
                            {stage === 'goal' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 1.1, delay: 0.2 }}
                                    className="w-full"
                                >
                                    <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-3">
                                        YOUR NORTH STAR
                                    </div>
                                    <div className="border-l-2 border-[color:var(--lavender-deep)] pl-4 text-left">
                                        <p
                                            className="display-italic text-[color:var(--ink)]"
                                            style={{ fontSize: 'clamp(18px, 2.1vw, 28px)', lineHeight: 1.3 }}
                                        >
                                            “{user?.goal || 'To learn a little more each day.'}”
                                        </p>
                                        <div className="mono text-[10px] text-[color:var(--moss-deep)] mt-3">
                                            — {firstName}, {grade}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* "I'm ready" glowing button — anchored to the RIGHT page, centred at bottom, below the quote (clear of the spine) */}
                    <AnimatePresence>
                        {showReady && !zoom && (
                            <motion.button
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.7, ease: [0.83, 0, 0.17, 1] }}
                                onClick={enter}
                                data-testid="im-ready"
                                className="absolute left-1/2 -translate-x-1/2 bottom-10 z-20 px-8 py-3 rounded-full text-sm mono whitespace-nowrap"
                                style={{
                                    background: 'var(--lavender)',
                                    color: 'var(--moss-dark)',
                                    border: '1px solid rgba(255,255,255,0.6)',
                                    boxShadow:
                                        '0 0 0 0 rgba(183,156,255,0.7), 0 8px 24px -6px rgba(0,0,0,0.4)',
                                }}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.97 }}
                            >
                                <motion.span
                                    animate={{
                                        textShadow: [
                                            '0 0 0px rgba(42,49,21,0)',
                                            '0 0 6px rgba(42,49,21,0.3)',
                                            '0 0 0px rgba(42,49,21,0)',
                                        ],
                                    }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    I&apos;m ready →
                                </motion.span>
                                {/* pulsing glow ring */}
                                <motion.span
                                    className="absolute inset-0 rounded-full -z-10"
                                    animate={{
                                        boxShadow: [
                                            '0 0 0 0 rgba(183,156,255,0.6)',
                                            '0 0 0 14px rgba(183,156,255,0)',
                                        ],
                                    }}
                                    transition={{ duration: 1.8, repeat: Infinity }}
                                />
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* Mobile: single page — greeting + goal + I'm ready stacked, no overlap */}
            <motion.div
                animate={zoom ? { scale: 1.6, filter: 'brightness(1.4)' } : { scale: 1 }}
                transition={{ duration: 1.0, ease: [0.6, 0, 0.2, 1] }}
                className="md:hidden relative folio-shadow rounded-sm paper grain-light overflow-hidden"
                style={{
                    width: 'min(420px, 92vw)',
                    height: 'min(78vh, 720px)',
                    transformOrigin: 'center center',
                }}
                data-testid="reveal-single-page"
            >
                <div className="relative h-full flex flex-col items-center justify-between p-6 text-center">
                    {/* Top: kicker + greeting */}
                    <div className="w-full">
                        <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-3">
                            A folio, freshly bound
                        </div>
                        <motion.h2
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9 }}
                            className="display text-[color:var(--ink)]"
                            style={{ fontSize: 'clamp(24px, 8vw, 36px)', lineHeight: 1.05 }}
                        >
                            Welcome,{' '}
                            <span className="display-italic" style={{ color: 'var(--lavender-deep)' }}>
                                {firstName}.
                            </span>
                        </motion.h2>
                    </div>

                    {/* Middle: goal block (fades in with stage) */}
                    <AnimatePresence>
                        {stage === 'goal' && (
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9 }}
                                className="w-full my-4"
                            >
                                <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-2">
                                    YOUR NORTH STAR
                                </div>
                                <div className="border-l-2 border-[color:var(--lavender-deep)] pl-3 text-left">
                                    <p
                                        className="display-italic text-[color:var(--ink)]"
                                        style={{ fontSize: 'clamp(15px, 4.4vw, 20px)', lineHeight: 1.3 }}
                                    >
                                        “{user?.goal || 'To learn a little more each day.'}”
                                    </p>
                                    <div className="mono text-[9px] text-[color:var(--moss-deep)] mt-2">
                                        — {firstName}, {grade}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Bottom: I'm ready button in dedicated space (no overlap) */}
                    <AnimatePresence>
                        {showReady && !zoom && (
                            <motion.button
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.6 }}
                                onClick={enter}
                                data-testid="im-ready-mobile"
                                className="relative w-full max-w-[240px] px-6 py-3 rounded-full text-sm mono whitespace-nowrap"
                                style={{
                                    background: 'var(--lavender)',
                                    color: 'var(--moss-dark)',
                                    border: '1px solid rgba(255,255,255,0.6)',
                                    boxShadow: '0 8px 24px -6px rgba(0,0,0,0.4)',
                                }}
                                whileTap={{ scale: 0.97 }}
                            >
                                I&apos;m ready →
                                <motion.span
                                    className="absolute inset-0 rounded-full -z-10 pointer-events-none"
                                    animate={{
                                        boxShadow: [
                                            '0 0 0 0 rgba(183,156,255,0.6)',
                                            '0 0 0 12px rgba(183,156,255,0)',
                                        ],
                                    }}
                                    transition={{ duration: 1.8, repeat: Infinity }}
                                />
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* WHITE LIGHT WASH */}
            <AnimatePresence>
                {leaving && (
                    <motion.div
                        key="wash"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5, ease: 'easeIn' }}
                        className="fixed inset-0 z-[120] pointer-events-none"
                        style={{ background: '#ffffff' }}
                        data-testid="white-wash"
                    />
                )}
            </AnimatePresence>
        </motion.div>
    );
}
